import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ITicketResponse } from '@core/models/ticket.model';
import { ApiService } from '@core/services/api/api.service';
import { environment } from '@environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IApiErrorResponse, ITargetAppItem, ITargetAppWithProfiles } from '../models';
import { AuthService } from './auth.service';
import { Console } from './logger.service';
import { AppStore } from './store/app.store';
import { TargetAppsService } from './target-apps.service';
import { TicketService } from './ticket.service';
import { ToastService } from './toast.service';

/**
 * Risultato della validazione di una target app per SSO
 */
export interface SsoValidationResult {
  isValid: boolean;
  targetAppData?: ITargetAppItem;
  profilo?: ITargetAppWithProfiles['profiles'][number];
  errorMessage?: string;
}

/** `GET portale-agenzia/me`: il profilo portale è in `data.profile`. */
interface IPortaleMeData {
  profile?: {
    id_profilo?: number | string | null;
  } | null;
}

/**
 * Opzioni per la gestione del flusso SSO
 */
export interface SsoFlowOptions {
  /** URL di fallback in caso di errore (default: '/landing-page') */
  fallbackUrl?: string;
  /** Se true, mantiene target_app nel redirect di errore di generazione ticket
   * (generateTicketAndRedirectDirect / generateTicketNoProfilesAndRedirect).
   * Gli errori di validazione target app reindirizzano sempre a /login senza target_app.
   */
  preserveTargetAppOnError?: boolean;
  /** Se true, apre l'URL in una nuova tab; se false, reindirizza nella stessa pagina */
  openInNewTab?: boolean;
}

/**
 * Servizio centralizzato per la gestione del flusso SSO.
 * 
 * Questo servizio centralizza tutta la logica SSO per evitare duplicazioni
 * e garantire una gestione consistente degli errori e del retry logic.
 * 
 * @example
 * constructor(private ssoService: SsoService) {}
 * 
 * // Genera ticket e redirect per una target app
 * this.ssoService.generateTicketAndRedirect('po', {
 *   fallbackUrl: '/login',
 *   preserveTargetAppOnError: true
 * }).subscribe({
 *   next: () => console.log('Redirect effettuato'),
 *   error: (error) => console.error('Errore SSO:', error)
 * });
 */
@Injectable({
  providedIn: 'root'
})
export class SsoService {
  private readonly router = inject(Router);
  private readonly appStore = inject(AppStore);
  private readonly authService = inject(AuthService);
  private readonly apiService = inject(ApiService);
  private readonly ticketService = inject(TicketService);
  private readonly targetAppsService = inject(TargetAppsService);
  private readonly toastService = inject(ToastService);

  /**
   * Valida una target app per il flusso SSO.
   * Verifica esistenza, abilitazione e disponibilità di un profilo.
   * 
   * @param targetApp Chiave della target app da validare
   * @returns Observable con il risultato della validazione
   */
  validateTargetApp(targetApp: string): Observable<SsoValidationResult> {
    return this.targetAppsService.getTargetAppsWithProfiles().pipe(
      map((targetApps: ITargetAppWithProfiles[]): SsoValidationResult => {
        const normalizedTargetApp = targetApp.trim();
        Console.log('SsoService.validateTargetApp - start', { targetApp, normalizedTargetApp }, 'info');

        const buildTargetAppItem = (
          group: ITargetAppWithProfiles,
          key: string,
          overrideUrl?: string
        ): ITargetAppItem => ({
          key,
          app: {
            ...group.app,
            url: overrideUrl ?? group.app.url
          }
        });

        const allProfiles = targetApps.flatMap((g) => g.profiles ?? []);
        Console.log('SsoService.validateTargetApp - targetApps loaded', {
          totalGroups: targetApps.length,
          totalProfiles: allProfiles.length
        }, 'info');

        let targetAppData: ITargetAppItem | undefined;

        // 1. Cerca per chiave (key) all'interno delle chiavi del gruppo e dei profili
        for (const group of targetApps) {
          Console.log('SsoService.validateTargetApp - checking group', {
            groupName: group.nome,
            keys: group.keys,
            hasNoProfileApp: !!group.noProfileApp
          }, 'debug');
          // Preferisci il match diretto sui profili (che espongono l'URL corretto per quella key)
          const matchingProfileEntry = group.profiles.find(
            (p) => p.key === normalizedTargetApp
          );
          if (matchingProfileEntry) {
            Console.log('SsoService.validateTargetApp - matched profile by key', {
              key: matchingProfileEntry.key,
              url: matchingProfileEntry.url
            }, 'info');
            targetAppData = buildTargetAppItem(
              group,
              matchingProfileEntry.key,
              matchingProfileEntry.url
            );
            break;
          }

          // In assenza di profilo specifico, verifica la presenza della key tra quelle del gruppo
          if (
            group.noProfileApp?.key === normalizedTargetApp ||
            group.keys.includes(normalizedTargetApp)
          ) {
            Console.log('SsoService.validateTargetApp - matched group key/noProfileApp by key', {
              key: normalizedTargetApp
            }, 'info');
            targetAppData = buildTargetAppItem(group, normalizedTargetApp);
            break;
          }
        }

        // 2. Se non trovata per chiave, cerca per nome dell'app (case-insensitive)
        if (!targetAppData) {
          const lowerName = normalizedTargetApp.toLowerCase();
          const groupsWithSameName = targetApps.filter(
            (g) => g.nome && g.nome.toLowerCase() === lowerName
          );

          Console.log('SsoService.validateTargetApp - groupsWithSameName', {
            lowerName,
            count: groupsWithSameName.length
          }, 'info');

          if (groupsWithSameName.length > 0) {
            // Prova a trovare un gruppo/key per cui l'utente abbia un profilo disponibile
            for (const group of groupsWithSameName) {
              for (const key of group.keys) {
                const matchingProfile = allProfiles.find((p) => p.key === key);
                if (matchingProfile) {
                  const profileEntry = group.profiles.find((p) => p.key === key);
                  const url = profileEntry?.url ?? group.app.url;
                  Console.log('SsoService.validateTargetApp - matched by app name + available profile', {
                    groupName: group.nome,
                    key,
                    url
                  }, 'info');
                  targetAppData = buildTargetAppItem(group, key, url);
                  break;
                }
              }
              if (targetAppData) {
                break;
              }
            }

            // Se ancora non trovata, fallback: prendi il primo gruppo abilitato (o il primo in assoluto)
            if (!targetAppData) {
              const candidateGroup =
                groupsWithSameName.find((g) => g.app.abilitato) || groupsWithSameName[0];

              const candidateKey =
                candidateGroup.keys[0] ??
                candidateGroup.noProfileApp?.key ??
                normalizedTargetApp;

              const profileEntry = candidateGroup.profiles.find(
                (p) => p.key === candidateKey
              );
              const url = profileEntry?.url ?? candidateGroup.app.url;

              Console.log('SsoService.validateTargetApp - fallback group/app chosen', {
                groupName: candidateGroup.nome,
                candidateKey,
                url
              }, 'info');

              targetAppData = buildTargetAppItem(candidateGroup, candidateKey, url);
            }
          }
        }

        if (!targetAppData) {
          Console.log('SsoService.validateTargetApp - no targetAppData found', {
            normalizedTargetApp
          }, 'warn');
          return {
            isValid: false,
            errorMessage: 'Applicazione non disponibile.'
          };
        }

        if (!targetAppData.app.abilitato) {
          Console.log('SsoService.validateTargetApp - app not enabled', {
            key: targetAppData.key,
            appName: targetAppData.app.nome
          }, 'warn');
          return {
            isValid: false,
            targetAppData,
            errorMessage: 'Applicazione non abilitata.'
          };
        }

        // Trova il profilo corrispondente alla chiave della target app (ruolo)
        const profilo = allProfiles.find((p) => p.key === targetAppData!.key);

        // Se non è stato trovato un profilo ma esiste almeno una app con senza_profilo=true,
        // lasciamo la validazione fallire come prima. Il caso "senza profilo" viene gestito
        // esplicitamente dalla landing-page tramite TargetAppsService + generateTicketNoProfiles.
        if (!profilo) {
          Console.log('SsoService.validateTargetApp - no profile for app key', {
            appKey: targetAppData.key,
            availableProfiles: allProfiles.map((p) => p.key)
          }, 'warn');
          return {
            isValid: false,
            targetAppData,
            errorMessage:
              'Nessun profilo disponibile per questa applicazione: ' +
              targetAppData.key +
              '. Profili disponibili: ' +
              allProfiles.map((p) => p.ruolo).join(', ')
          };
        }

        Console.log('SsoService.validateTargetApp - validation success', {
          key: targetAppData.key,
          appName: targetAppData.app.nome,
          profileId: profilo.id_profilo
        }, 'info');

        return {
          isValid: true,
          targetAppData,
          profilo
        };
      }),
      catchError((error) => {
        Console.log('SsoService.validateTargetApp - catchError', { error }, 'err');
        return of({
          isValid: false,
          errorMessage: 'Errore nel recupero delle applicazioni disponibili.'
        } as SsoValidationResult);
      })
    );
  }

  /**
   * Per `app-portale` legge `id_profilo` da `GET portale-agenzia/me` e genera il ticket.
   * Non usa `sso/target-apps-profili`.
   */
  private generateTicketFromPortaleMe(targetApp: string, options: SsoFlowOptions): Observable<void> {
    return this.apiService.get<IPortaleMeData>('portale-agenzia/me').pipe(
      catchError((error: unknown) => {
        Console.log('SsoService.generateTicketFromPortaleMe - me error', { error }, 'err');
        this.toastService.presentaToastErrore('Errore nel recupero del profilo portale.');
        this.redirectAfterSsoFailure(targetApp, options);
        return throwError(() => error);
      }),
      switchMap((data) => {
        const idProfilo = Number(data?.profile?.id_profilo);
        if (!Number.isFinite(idProfilo) || idProfilo <= 0) {
          const message = 'Nessun profilo disponibile per questa applicazione: ' + targetApp;
          this.toastService.presentaToastErrore(message, 0);
          this.navigateToCleanLogin();
          return throwError(() => new Error(message));
        }
        Console.log('SsoService.generateTicketFromPortaleMe', { targetApp, idProfilo }, 'info');
        return this.generateTicketAndRedirectDirect(targetApp, idProfilo, '', options);
      })
    );
  }

  private redirectAfterSsoFailure(targetApp: string, options: SsoFlowOptions): void {
    const fallbackUrl = options.fallbackUrl || '/login';
    const preserveTargetApp = options.preserveTargetAppOnError ?? false;
    if (preserveTargetApp && fallbackUrl === '/login') {
      void this.router.navigateByUrl(`${fallbackUrl}?target_app=${encodeURIComponent(targetApp)}`);
      return;
    }
    void this.router.navigateByUrl(fallbackUrl);
  }

  /**
   * Genera un ticket SSO e reindirizza all'applicazione di destinazione.
   * Per `app-portale` il profilo arriva da `GET portale-agenzia/me`.
   * Le altre target app passano da `sso/target-apps-profili`.
   * Include retry logic automatico con refresh token su errori 401.
   *
   * @param targetApp Chiave della target app
   * @param options Opzioni per la gestione del flusso SSO
   * @returns Observable che completa quando il redirect è effettuato
   */
  generateTicketAndRedirect(targetApp: string, options: SsoFlowOptions = {}): Observable<void> {
    if (targetApp.trim().toLowerCase() === 'app-portale') {
      return this.generateTicketFromPortaleMe(targetApp.trim(), options);
    }
    return this.validateTargetApp(targetApp).pipe(
      switchMap((validation) => {
        if (!validation.isValid || !validation.targetAppData || !validation.profilo) {
          const isNoProfileError =
            !!validation.targetAppData &&
            !validation.profilo &&
            !!validation.errorMessage &&
            validation.errorMessage.startsWith('Nessun profilo disponibile per questa applicazione');

          if (validation.errorMessage) {
            if (isNoProfileError) {
              // duration = 0 => autohide disabilitato, resta visibile finché non si preme chiudi
              this.toastService.presentaToastErrore(validation.errorMessage, 0);
            } else {
              this.toastService.presentaToastErrore(validation.errorMessage);
            }
          }

          // Non è un errore di autenticazione: rimuove target_app dall'URL per evitare loop SSO.
          this.navigateToCleanLogin();

          return throwError(() => new Error(validation.errorMessage || 'Validazione target app fallita'));
        }

        const { targetAppData, profilo } = validation;
        
        // Leggi openInNewTab dalle opzioni o dalla proprietà nuova_pagina della target app
        const openInNewTab = options.openInNewTab ?? targetAppData.app.nuova_pagina ?? false;

        // Genera il ticket con retry logic
        // Usa la chiave della target app trovata, non il nome originale
        return this.generateTicketWithRetry(targetAppData.key, profilo.id_profilo).pipe(
          switchMap((response) => {
            const redirectUrl = this.redirectUrlFromTicketResponse(response);
            if (!redirectUrl) {
              this.toastService.presentaToastErrore('Errore: redirect_url non ricevuto dal server');
              if (environment.ENABLE_DEBUG_ALERTS) {
                alert('auth-app : Redirect a /login (redirect_url assente)');
              }
              this.navigateToCleanLogin();
              return of(void 0);
            }
            if (environment.ENABLE_DEBUG_ALERTS) {
              console.info('[SSO redirect]', { redirectUrl, ticket: response.ticket });
              alert('auth-app : Redirect esterno a ' + redirectUrl + ' (SSO generateTicketAndRedirect)');
            }
            try {
              this.redirectToUrl(redirectUrl, openInNewTab);
            } catch (error) {
              // Se il pop-up viene bloccato, fallback a redirect nella stessa pagina
              if (error instanceof Error && error.message.includes('Pop-up bloccato')) {
                this.toastService.presentaToastWarning(
                  'Pop-up bloccato dal browser. L\'applicazione si aprirà nella stessa finestra.'
                );
                window.location.href = redirectUrl;
              } else {
                throw error;
              }
            }
            return of(void 0);
          })
        );
      }),
      catchError((error) => {
        // Gestione errori già fatta nei metodi chiamati
        return throwError(() => error);
      })
    );
  }

  /**
   * Genera un ticket SSO e reindirizza direttamente usando id_profilo e urlApp già disponibili.
   * Utile quando i dati sono già stati validati (es. landing-page).
   * Include retry logic automatico con refresh token su errori 401.
   *
   * @param targetApp Chiave della target app
   * @param idProfilo ID del profilo utente (già validato)
   * @param urlApp URL dell'applicazione di destinazione
   * @param options Opzioni per la gestione del flusso SSO
   * @returns Observable che completa quando il redirect è effettuato
   */
  generateTicketAndRedirectDirect(
    targetApp: string,
    idProfilo: number,
    urlApp: string,
    options: SsoFlowOptions = {}
  ): Observable<void> {
    const openInNewTab = options.openInNewTab ?? false;
    
    Console.log('SsoService.generateTicketAndRedirectDirect', {
      targetApp,
      idProfilo,
      urlApp,
      openInNewTab
    }, 'info');

    return this.generateTicketWithRetry(targetApp, idProfilo).pipe(
      switchMap((response) => {
        if (!response?.ticket || typeof response.ticket !== 'string') {
          this.toastService.presentaToastErrore('Errore: ticket non ricevuto dal server');
          if (environment.ENABLE_DEBUG_ALERTS) {
            alert('auth-app : Redirect a ' + (options.fallbackUrl || '/landing-page') + ' (ticket non ricevuto)');
          }
          this.router.navigateByUrl(options.fallbackUrl || '/landing-page');
          return of(void 0);
        }
        const redirectUrl = this.redirectUrlFromTicketResponse(response);
        if (!redirectUrl) {
          this.toastService.presentaToastErrore('Errore: redirect_url non ricevuto dal server');
          if (environment.ENABLE_DEBUG_ALERTS) {
            alert('auth-app : Redirect a ' + (options.fallbackUrl || '/landing-page') + ' (redirect_url assente)');
          }
          this.router.navigateByUrl(options.fallbackUrl || '/landing-page');
          return of(void 0);
        }

        Console.log('SsoService.generateTicketAndRedirectDirect.redirect', {
          targetApp,
          idProfilo,
          urlApp,
          ticket: response.ticket,
          redirectUrl,
          openInNewTab
        }, 'info');

        if (environment.ENABLE_DEBUG_ALERTS) {
          alert('auth-app : Redirect esterno a ' + redirectUrl + ' (SSO generateTicketAndRedirectDirect)');
        }
        try {
          this.redirectToUrl(redirectUrl, openInNewTab);
        } catch (error) {
          // Se il pop-up viene bloccato, fallback a redirect nella stessa pagina
          if (error instanceof Error && error.message.includes('Pop-up bloccato')) {
            this.toastService.presentaToastWarning(
              'Pop-up bloccato dal browser. L\'applicazione si aprirà nella stessa finestra.'
            );
            window.location.href = redirectUrl;
          } else {
            throw error;
          }
        }
        return of(void 0);
      }),
      catchError((error) => {
        const fallbackUrl = options.fallbackUrl || '/landing-page';
        const preserveTargetApp = options.preserveTargetAppOnError ?? false;

        if (preserveTargetApp && fallbackUrl === '/login') {
          if (environment.ENABLE_DEBUG_ALERTS) {
            alert('auth-app : Redirect a ' + fallbackUrl + '?target_app=' + encodeURIComponent(targetApp) + ' (errore SSO)');
          }
          this.router.navigateByUrl(`${fallbackUrl}?target_app=${encodeURIComponent(targetApp)}`);
        } else {
          if (environment.ENABLE_DEBUG_ALERTS) {
            alert('auth-app : Redirect a ' + fallbackUrl + ' (errore SSO)');
          }
          this.router.navigateByUrl(fallbackUrl);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Genera un ticket SSO e reindirizza all'applicazione di destinazione
   * per il caso in cui non esistono profili (usa endpoint dedicato senza id_profilo).
   *
   * @param targetApp Chiave della target app
   * @param urlApp URL dell'applicazione di destinazione
   * @param options Opzioni per la gestione del flusso SSO
   * @returns Observable che completa quando il redirect è effettuato
   */
  generateTicketNoProfilesAndRedirect(
    targetApp: string,
    urlApp: string,
    options: SsoFlowOptions = {}
  ): Observable<void> {
    const openInNewTab = options.openInNewTab ?? false;

    Console.log('SsoService.generateTicketNoProfilesAndRedirect', {
      targetApp,
      urlApp,
      openInNewTab
    }, 'info');

    return this.generateTicketNoProfilesWithRetry(targetApp).pipe(
      switchMap((response) => {
        if (!response?.ticket || typeof response.ticket !== 'string') {
          this.toastService.presentaToastErrore('Errore: ticket non ricevuto dal server');
          if (environment.ENABLE_DEBUG_ALERTS) {
            alert('auth-app : Redirect a ' + (options.fallbackUrl || '/landing-page') + ' (ticket non ricevuto - no profiles)');
          }
          this.router.navigateByUrl(options.fallbackUrl || '/landing-page');
          return of(void 0);
        }

        const redirectUrl = this.redirectUrlFromTicketResponse(response);
        if (!redirectUrl) {
          this.toastService.presentaToastErrore('Errore: redirect_url non ricevuto dal server');
          if (environment.ENABLE_DEBUG_ALERTS) {
            alert('auth-app : Redirect a ' + (options.fallbackUrl || '/landing-page') + ' (redirect_url assente - no profiles)');
          }
          this.router.navigateByUrl(options.fallbackUrl || '/landing-page');
          return of(void 0);
        }

        Console.log('SsoService.generateTicketNoProfilesAndRedirect.redirect', {
          targetApp,
          urlApp,
          ticket: response.ticket,
          redirectUrl,
          openInNewTab
        }, 'info');

        if (environment.ENABLE_DEBUG_ALERTS) {
          alert('auth-app : Redirect esterno a ' + redirectUrl + ' (SSO generateTicketNoProfilesAndRedirect)');
        }

        try {
          this.redirectToUrl(redirectUrl, openInNewTab);
        } catch (error) {
          // Se il pop-up viene bloccato, fallback a redirect nella stessa pagina
          if (error instanceof Error && error.message.includes('Pop-up bloccato')) {
            this.toastService.presentaToastWarning(
              'Pop-up bloccato dal browser. L\'applicazione si aprirà nella stessa finestra.'
            );
            window.location.href = redirectUrl;
          } else {
            throw error;
          }
        }

        return of(void 0);
      }),
      catchError((error) => {
        const fallbackUrl = options.fallbackUrl || '/landing-page';
        const preserveTargetApp = options.preserveTargetAppOnError ?? false;

        if (preserveTargetApp && fallbackUrl === '/login') {
          if (environment.ENABLE_DEBUG_ALERTS) {
            alert('auth-app : Redirect a ' + fallbackUrl + '?target_app=' + encodeURIComponent(targetApp) + ' (errore SSO no profiles)');
          }
          this.router.navigateByUrl(`${fallbackUrl}?target_app=${encodeURIComponent(targetApp)}`);
        } else {
          if (environment.ENABLE_DEBUG_ALERTS) {
            alert('auth-app : Redirect a ' + fallbackUrl + ' (errore SSO no profiles)');
          }
          this.router.navigateByUrl(fallbackUrl);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * URL di ritorno verso l'app client: usa esclusivamente `redirect_url` della risposta API.
   */
  private redirectUrlFromTicketResponse(response: ITicketResponse | null | undefined): string | null {
    const url = typeof response?.redirect_url === 'string' ? response.redirect_url.trim() : '';
    return url || null;
  }

  /**
   * Restituisce un nome stabile per la finestra SSO, così i click ripetuti
   * verso la stessa app riusano la scheda già aperta.
   */
  private getSsoWindowName(url: string): string {
    try {
      const hostname = new URL(url).hostname;
      return hostname ? `amcf-${hostname}` : 'amcf-target';
    } catch {
      return 'amcf-target';
    }
  }

  /**
   * Gestisce il redirect all'URL specificato, aprendo in nuova tab o nella stessa pagina.
   * Utilizzato solo da generateTicketAndRedirect e generateTicketAndRedirectDirect.
   *
   * Con `openInNewTab`, usa una finestra nominata (`amcf-{hostname}`) per riusare la stessa
   * scheda quando l'utente apre più volte la stessa app dalla landing.
   *
   * @param url URL a cui reindirizzare
   * @param openInNewTab Se true, apre in una nuova tab; se false, reindirizza nella stessa pagina
   * @throws Error se il pop-up viene bloccato dal browser quando openInNewTab è true
   */
  private redirectToUrl(url: string, openInNewTab: boolean): void {
    if (openInNewTab) {
      const windowName = this.getSsoWindowName(url);
      const newWindow = window.open(url, windowName);
      // Verifica se il pop-up è stato bloccato dal browser
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        throw new Error('Pop-up bloccato dal browser. Impossibile aprire la nuova finestra.');
      }
      try {
        newWindow.focus();
      } catch {
        // focus può fallire in contesti cross-origin; il redirect resta valido
      }
    } else {
      window.location.href = url;
    }
  }

  /**
   * Reindirizza a /login senza query `target_app`.
   * Usato quando la validazione SSO fallisce per mancanza di accesso all'app (non per sessione scaduta).
   */
  private navigateToCleanLogin(): void {
    if (environment.ENABLE_DEBUG_ALERTS) {
      alert('auth-app : Redirect a /login pulito (validazione target app fallita, target_app rimosso)');
    }
    void this.router.navigateByUrl('/login', { replaceUrl: true });
  }

  /**
   * Genera un ticket SSO con retry logic automatico.
   * Se riceve un errore 401, tenta il refresh del token e riprova una volta.
   * 
   * @param targetApp Chiave della target app
   * @param idProfilo ID del profilo utente
   * @param didRefresh Flag interno per evitare loop infiniti di retry
   * @returns Observable con la risposta del ticket
   */
  private generateTicketWithRetry(
    targetApp: string,
    idProfilo: number,
    didRefresh: boolean = false
  ): Observable<ITicketResponse> {
    return this.ticketService.generateTicket(targetApp, idProfilo).pipe(
      catchError((error: IApiErrorResponse) => {
        const statusCode = (error as any)?.status_code ?? (error as any)?.status;
        
        // Se è un 401 e non abbiamo ancora fatto refresh, prova a refreshare il token
        // Solo se rememberMe è attivo; altrimenti effettua logout.
        if (statusCode === 401 && !didRefresh) {
          const rememberMe = this.appStore.rememberMe();
          if (!rememberMe) {
            // Se rememberMe non è attivo, non tentare il refresh:
            // esegui logout e segnala la scadenza della sessione.
            this.toastService.presentaToastErrore('Sessione scaduta, effettua nuovamente il login');
            this.authService.logout().subscribe();
            return throwError(() => new Error('Sessione scaduta, rememberMe non attivo'));
          }
          
          return this.authService.refreshToken().pipe(
            switchMap(() => {
              // Riprova la generazione del ticket dopo il refresh
              return this.generateTicketWithRetry(targetApp, idProfilo, true);
            }),
            catchError((refreshError) => {
              // Se il refresh fallisce, mostra errore e rilancia
              this.toastService.presentaToastErrore('Sessione scaduta, effettua nuovamente il login');
              return throwError(() => refreshError);
            })
          );
        }

        // Per altri errori o se abbiamo già fatto refresh, mostra errore e rilancia
        Console.error('SsoService', { message: 'Errore nella generazione del ticket', error });
        const errorMessage = error?.message || 'Errore nella generazione del ticket. Riprova.';
        this.toastService.presentaToastErrore(errorMessage);
        return throwError(() => error);
      })
    );
  }

  /**
   * Genera un ticket SSO con retry logic automatico per il caso senza profili.
   * Se riceve un errore 401, tenta il refresh del token e riprova una volta.
   *
   * @param targetApp Chiave della target app
   * @param didRefresh Flag interno per evitare loop infiniti di retry
   * @returns Observable con la risposta del ticket
   */
  private generateTicketNoProfilesWithRetry(
    targetApp: string,
    didRefresh: boolean = false
  ): Observable<ITicketResponse> {
    return this.ticketService.generateTicketNoProfiles(targetApp).pipe(
      catchError((error: IApiErrorResponse) => {
        const statusCode = (error as any)?.status_code ?? (error as any)?.status;

        // Se è un 401 e non abbiamo ancora fatto refresh, prova a refreshare il token
        // Solo se rememberMe è attivo; altrimenti effettua logout.
        if (statusCode === 401 && !didRefresh) {
          const rememberMe = this.appStore.rememberMe();
          if (!rememberMe) {
            // Se rememberMe non è attivo, non tentare il refresh:
            // esegui logout e segnala la scadenza della sessione.
            this.toastService.presentaToastErrore('Sessione scaduta, effettua nuovamente il login');
            this.authService.logout().subscribe();
            return throwError(() => new Error('Sessione scaduta, rememberMe non attivo'));
          }

          return this.authService.refreshToken().pipe(
            switchMap(() => {
              // Riprova la generazione del ticket dopo il refresh
              return this.generateTicketNoProfilesWithRetry(targetApp, true);
            }),
            catchError((refreshError) => {
              // Se il refresh fallisce, mostra errore e rilancia
              this.toastService.presentaToastErrore('Sessione scaduta, effettua nuovamente il login');
              return throwError(() => refreshError);
            })
          );
        }

        // Per altri errori o se abbiamo già fatto refresh, mostra errore e rilancia
        Console.error('SsoService', { message: 'Errore nella generazione del ticket (no profiles)', error });
        const errorMessage = (error as any)?.message || 'Errore nella generazione del ticket. Riprova.';
        this.toastService.presentaToastErrore(errorMessage);
        return throwError(() => error);
      })
    );
  }
}
